import { Check, Mail, MapPin } from "lucide-react";
import { FieldWrapper } from "@/components/ui/FieldWrapper";
import Input from "@/components/ui/Input";
import Select from "@/components/ui/Select";
import Textarea from "@/components/ui/Textarea";
import PrefixInput from "@/pages/retailer/create-mandate/components/PrefixInput";
import { BUSINESS_TYPES, NATURE_OPTIONS, STATES, YEARS } from "../data";
import type { FormErrors, KycForm, SetField } from "../types";

interface Props {
  form: KycForm;
  errors: FormErrors;
  setField: SetField;
}

export default function BusinessDetailsStep({ form, errors, setField }: Props) {
  return (
    <div>
      <h3 className="text-lg font-semibold text-ink">Business Details</h3>
      <p className="text-[13px] text-ink-secondary">Tell us about your finance business</p>

      {/* Business type */}
      <div className="mt-6">
        <p className="mb-1.5 text-[13px] font-medium text-ink-secondary">
          Business Type<span className="ml-0.5 text-danger">*</span>
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {BUSINESS_TYPES.map((t) => {
            const selected = form.businessType === t.value;
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => setField("businessType", t.value)}
                className={`relative rounded-lg border p-4 text-left transition-colors cursor-pointer ${
                  selected
                    ? "border-primary-500 bg-primary-50"
                    : "border-slate-300 bg-white hover:border-slate-400"
                }`}
              >
                {selected && (
                  <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-primary-500 text-white">
                    <Check className="h-3.5 w-3.5" strokeWidth={3} />
                  </span>
                )}
                <span
                  className={`flex h-9 w-9 items-center justify-center rounded-lg ${
                    selected ? "bg-primary-100 text-primary-600" : "bg-slate-100 text-ink-secondary"
                  }`}
                >
                  <t.icon className="h-5 w-5" />
                </span>
                <p className="mt-3 text-sm font-medium text-ink">{t.value}</p>
                <p className="text-xs text-ink-muted">{t.sub}</p>
              </button>
            );
          })}
        </div>
        {errors.businessType && (
          <p className="mt-1.5 text-xs text-danger">{errors.businessType}</p>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-x-5 gap-y-5 md:grid-cols-2">
        <Input
          label="Legal Business Name"
          required
          error={errors.legalBusinessName}
          placeholder="Name as per registration"
          id="legalBusinessName"
          value={form.legalBusinessName}
          onChange={(e) => setField("legalBusinessName", e.target.value)}
        />
        <Input
          label="Trade or Brand Name"
          error={errors.tradeName}
          placeholder="Optional"
          id="tradeName"
          value={form.tradeName}
          onChange={(e) => setField("tradeName", e.target.value)}
        />
        <Input
          label="Business PAN"
          required
          error={errors.businessPan}
          placeholder="Format: ABCDE1234F"
          helper="Format: ABCDE1234F"
          id="businessPan"
          maxLength={10}
          value={form.businessPan}
          onChange={(e) => setField("businessPan", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
        />
        <Input
          label="GST Number"
          error={errors.gstNumber}
          placeholder="Format: 07ABCDE1234F1Z5"
          helper="Leave blank if not registered under GST"
          id="gstNumber"
          maxLength={15}
          value={form.gstNumber}
          onChange={(e) => setField("gstNumber", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
        />
        <Select
          label="Year of Incorporation"
          required
          placeholder="Select year"
          options={YEARS}
          error={errors.yearOfIncorporation}
          id="yearOfIncorporation"
          value={form.yearOfIncorporation}
          onChange={(e) => setField("yearOfIncorporation", e.target.value)}
        />
        <Select
          label="Nature of Business"
          required
          placeholder="Select nature"
          options={NATURE_OPTIONS}
          error={errors.natureOfBusiness}
          id="natureOfBusiness"
          value={form.natureOfBusiness}
          onChange={(e) => setField("natureOfBusiness", e.target.value)}
        />
        <div className="md:col-span-2">
          <Textarea
            label="Registered Address"
            required
            error={errors.registeredAddress}
            placeholder="Full registered address"
            id="registeredAddress"
            rows={2}
            value={form.registeredAddress}
            onChange={(e) => setField("registeredAddress", e.target.value)}
          />
        </div>
        <Input
          label="City"
          required
          error={errors.city}
          placeholder="Enter city"
          id="city"
          prefixIcon={<MapPin className="h-4 w-4" />}
          value={form.city}
          onChange={(e) => setField("city", e.target.value)}
        />
        <Select
          label="State"
          required
          placeholder="Select state"
          options={STATES}
          error={errors.state}
          id="state"
          value={form.state}
          onChange={(e) => setField("state", e.target.value)}
        />
        <FieldWrapper
          label="Pincode"
          required
          error={errors.pincode}
          htmlFor="pincode"
        >
          <input
            id="pincode"
            inputMode="numeric"
            maxLength={6}
            placeholder="6-digit pincode"
            value={form.pincode}
            onChange={(e) => setField("pincode", e.target.value.replace(/\D/g, ""))}
            className={`h-[42px] w-full rounded-lg border bg-white px-3.5 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 ${
              errors.pincode
                ? "border-danger focus:ring-danger/30"
                : "border-slate-300 focus:border-primary-500 focus:ring-primary-500/30"
            }`}
          />
        </FieldWrapper>
        <FieldWrapper
          label="Business Mobile"
          required
          error={errors.businessMobile}
          htmlFor="businessMobile"
        >
          <PrefixInput
            prefix="+91"
            id="businessMobile"
            inputMode="numeric"
            maxLength={10}
            placeholder="10-digit mobile number"
            error={!!errors.businessMobile}
            value={form.businessMobile}
            onChange={(e) => setField("businessMobile", e.target.value.replace(/\D/g, ""))}
          />
        </FieldWrapper>
        <Input
          label="Business Email"
          required
          type="email"
          error={errors.businessEmail}
          placeholder="business@email.com"
          id="businessEmail"
          prefixIcon={<Mail className="h-4 w-4" />}
          value={form.businessEmail}
          onChange={(e) => setField("businessEmail", e.target.value)}
        />
      </div>
    </div>
  );
}